/**
 * InvoiceCollection / BillCollection — the system-document views inside the
 * Documents section. Each pulls its source list (`invoices.list` / `bills.list`,
 * non-draft only) and renders read-only rows that download the rendered PDF.
 * No new backend: reuses the existing list + `/api/<doc>/:id/pdf` routes.
 */
import { Receipt, ShoppingCart } from "lucide-react";
import { Alert, type StatusTone } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { RecordDocList, type RecordDoc } from "./record-doc-list.tsx";

const INVOICE_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  issued: { label: "Issued", tone: "sky" },
  partially_paid: { label: "Part-paid", tone: "warn" },
  paid: { label: "Paid", tone: "ok" },
  overdue: { label: "Overdue", tone: "bad" },
  void: { label: "Void", tone: "muted" },
  reversed: { label: "Reversed", tone: "muted" },
};

const BILL_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  review: { label: "In review", tone: "sky" },
  approval_pending: { label: "Pending", tone: "warn" },
  ready_to_pay: { label: "To pay", tone: "warn" },
  paid: { label: "Paid", tone: "ok" },
  void: { label: "Void", tone: "muted" },
};

function Empty({ kind }: { kind: "invoices" | "bills" }) {
  const Icon = kind === "invoices" ? Receipt : ShoppingCart;
  return (
    <div className="rounded-md border border-dashed border-line bg-paper px-6 py-14 text-center">
      <Icon size={26} className="mx-auto text-line-2" aria-hidden />
      <p className="mt-3 text-base font-semibold text-ink">
        No {kind === "invoices" ? "issued invoices" : "vendor bills"} yet
      </p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted">
        {kind === "invoices"
          ? "Issued invoices appear here automatically — download or print their PDFs."
          : "Recorded vendor bills appear here automatically."}
      </p>
    </div>
  );
}

export function InvoiceCollection() {
  const q = trpc.invoices.list.useQuery({ limit: 50 });
  if (q.error) return <Alert intent="bad" title="Couldn't load invoices" description={q.error.message} />;
  if (q.isLoading) return <p className="text-sm text-muted">Loading invoices…</p>;

  const items: RecordDoc[] = (q.data?.items ?? [])
    .filter((inv) => inv.status !== "draft")
    .map((inv) => {
      const s = INVOICE_STATUS[inv.status] ?? { label: inv.status, tone: "muted" as StatusTone };
      return {
        id: inv.id,
        kind: "invoice" as const,
        title: inv.docNumber ?? "Draft invoice",
        party: inv.customerName,
        date: inv.issuedOn,
        amountPaise: inv.totalPaise,
        status: inv.status,
        statusLabel: s.label,
        statusTone: s.tone,
      };
    });

  if (items.length === 0) return <Empty kind="invoices" />;
  return <RecordDocList items={items} footerLabel="Invoices" />;
}

export function BillCollection() {
  const q = trpc.bills.list.useQuery({ limit: 50 });
  if (q.error) return <Alert intent="bad" title="Couldn't load bills" description={q.error.message} />;
  if (q.isLoading) return <p className="text-sm text-muted">Loading bills…</p>;

  const items: RecordDoc[] = (q.data?.items ?? [])
    .filter((b) => b.status !== "draft")
    .map((b) => {
      const s = BILL_STATUS[b.status] ?? { label: b.status, tone: "muted" as StatusTone };
      return {
        id: b.id,
        kind: "bill" as const,
        title: b.vendorDocNumber || "Bill",
        party: b.vendorName,
        date: b.receivedOn,
        amountPaise: b.totalPaise,
        status: b.status,
        statusLabel: s.label,
        statusTone: s.tone,
      };
    });

  if (items.length === 0) return <Empty kind="bills" />;
  return <RecordDocList items={items} footerLabel="Bills" />;
}
